import { LitElement, css, html, nothing } from "lit";
import type { TemplateResult } from "lit";
import type { Article } from "@newsfeed/shared";
import { sharedStyles } from "../styles.js";
import "./article-row.js";

/** Local-midnight timestamp (ms) for a given instant. */
function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Presentational newest-first article list with an unread/all filter toggle.
 * Article intent events (`open-article`, `toggle-read`, `toggle-keep`) bubble
 * from the rows through this element to the containing view. Emits
 * `filter-change` (detail `{ filter }`) so the view can refetch.
 *
 * When `grouped` is set, articles are split into Today / Yesterday / Earlier
 * sections by local calendar day (empty sections omitted).
 */
export class NfArticleList extends LitElement {
  static override properties = {
    articles: { attribute: false },
    filter: { type: String },
    loading: { type: Boolean },
    heading: { type: String },
    pending: { attribute: false },
    grouped: { type: Boolean },
  };

  articles: Article[] = [];
  filter: "all" | "unread" = "all";
  loading = false;
  heading = "";
  /** Which state-change action is in flight for a given article id. */
  pending: Record<string, "read" | "keep"> = {};
  grouped = false;

  static override styles = [
    sharedStyles,
    css`
      header {
        display: flex;
        align-items: center;
        gap: var(--nf-space-12);
        margin-bottom: var(--nf-space-8);
      }
      h2 {
        font-size: var(--nf-font-18);
        flex: 1;
      }
      .toggle button.active {
        background: var(--nf-accent);
        color: var(--nf-accent-fg);
        border-color: var(--nf-accent);
      }
      .list {
        background: var(--nf-surface);
        border: 1px solid var(--nf-border);
        border-radius: var(--nf-radius);
      }
      .list__heading {
        font-size: var(--nf-font-13);
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--nf-muted);
        margin: var(--nf-space-16) 0 var(--nf-space-8);
      }
      .list__heading:first-child {
        margin-top: 0;
      }
      .toggle {
        gap: var(--nf-space-4);
      }
    `,
  ];

  private setFilter(filter: "all" | "unread"): void {
    if (filter === this.filter) return;
    this.dispatchEvent(
      new CustomEvent("filter-change", {
        detail: { filter },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Bucket articles into Today / Yesterday / Earlier by local calendar day. */
  private sections(): { label: string; items: Article[] }[] {
    const todayStart = startOfDay(Date.now());
    const yesterdayStart = todayStart - 86_400_000;

    const today: Article[] = [];
    const yesterday: Article[] = [];
    const earlier: Article[] = [];
    for (const article of this.articles) {
      if (article.effectiveDate >= todayStart) today.push(article);
      else if (article.effectiveDate >= yesterdayStart) yesterday.push(article);
      else earlier.push(article);
    }

    const result: { label: string; items: Article[] }[] = [];
    if (today.length > 0) result.push({ label: "Today", items: today });
    if (yesterday.length > 0)
      result.push({ label: "Yesterday", items: yesterday });
    if (earlier.length > 0) result.push({ label: "Earlier", items: earlier });
    return result;
  }

  override render(): TemplateResult {
    return html`
      <header>
        ${this.heading ? html`<h2>${this.heading}</h2>` : nothing}
        <div class="toggle row">
          <button
            class=${this.filter === "all" ? "active" : ""}
            @click=${() => this.setFilter("all")}
          >
            All
          </button>
          <button
            class=${this.filter === "unread" ? "active" : ""}
            @click=${() => this.setFilter("unread")}
          >
            Unread
          </button>
        </div>
      </header>
      ${
        this.loading
          ? html`<div class="empty">Loading…</div>`
          : this.articles.length === 0
            ? html`<div class="empty">No articles.</div>`
            : this.grouped
              ? html`${this.sections().map(
                  (section) => html`
                    <h3 class="list__heading">${section.label}</h3>
                    <div class="list">
                      ${section.items.map(
                        (article) =>
                          html`<nf-article-row
                          .article=${article}
                          .pending=${this.pending[article.id] ?? null}
                        ></nf-article-row>`,
                      )}
                    </div>
                  `,
                )}`
              : html`<div class="list">
                  ${this.articles.map(
                    (article) =>
                      html`<nf-article-row
                      .article=${article}
                      .pending=${this.pending[article.id] ?? null}
                    ></nf-article-row>`,
                  )}
                </div>`
      }
    `;
  }
}

customElements.define("nf-article-list", NfArticleList);

declare global {
  interface HTMLElementTagNameMap {
    "nf-article-list": NfArticleList;
  }
}
